/**
 * service-finance client.
 *
 * Endpoints used by the operator console (slice scope):
 *   GET   /v1/finance/settlements          — paginated settlement list
 *   GET   /v1/finance/settlements/:id      — fetch detail + line items
 *
 * The chart-of-accounts + journal endpoints (POST /v1/journals, GET /v1/accounts)
 * are out of scope for this client today — the dashboard tile only sums
 * settled net amounts. Add them when an operator screen needs them.
 */
import { env } from '../env';

import { request } from './client';

const baseUrl = (): string => env.serviceFinanceBaseUrl;

export type SettlementStatus = 'PENDING' | 'APPROVED' | 'PAID' | 'FAILED' | 'CANCELLED';
export type CounterpartyType =
  | 'OPERATOR'
  | 'PARTNER'
  | 'FRANCHISE'
  | 'DISBURSEMENT_RECIPIENT';

export interface SettlementLineItem {
  readonly id: string;
  readonly settlementId: string;
  readonly journalEntryId: string | null;
  readonly description: string;
   
  readonly amountMinor: number;
  readonly side: string;
  readonly createdAt: string;
}

export interface Settlement {
  readonly id: string;
  readonly settlementNumber: string;
  readonly counterpartyType: CounterpartyType;
  readonly counterpartyId: string;
  readonly counterpartyName: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly currency: string;
   
  readonly grossAmountMinor: number;
   
  readonly feeAmountMinor: number;
   
  readonly netAmountMinor: number;
   
  readonly txnCount: number;
  readonly status: SettlementStatus;
  readonly approvedAt: string | null;
  readonly approvedByUserId: string | null;
  readonly paidAt: string | null;
  readonly paymentReference: string | null;
  readonly failureReason: string | null;
  readonly metadata: unknown;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly lineItems?: readonly SettlementLineItem[];
}

export interface ListSettlementsResponse {
  readonly items: readonly Settlement[];
  // eslint-disable-next-line upmp/no-number-money -- row count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListSettlementsQuery {
  readonly counterpartyType?: CounterpartyType;
  readonly counterpartyId?: string;
  readonly status?: SettlementStatus;
  /** YYYY-MM-DD. */
  readonly from?: string;
  /** YYYY-MM-DD. */
  readonly to?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export async function listSettlements(
  q: ListSettlementsQuery = {},
): Promise<ListSettlementsResponse> {
  return request<ListSettlementsResponse>(baseUrl(), '/v1/finance/settlements', {
    method: 'GET',
    query: {
      counterpartyType: q.counterpartyType,
      counterpartyId: q.counterpartyId,
      status: q.status,
      from: q.from,
      to: q.to,
      page: q.page,
      pageSize: q.pageSize,
    },
  });
}

export async function getSettlement(id: string): Promise<Settlement> {
  return request<Settlement>(baseUrl(), `/v1/finance/settlements/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}
